import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/apiClient';

interface IpPool {
  id: string;
  name: string;
  ipAddress: string;
  poolType: 'DEDICATED' | 'SHARED';
  reputationScore: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PoolForm {
  name: string;
  ipAddress: string;
  poolType: string;
  reputationScore: number;
  isActive: boolean;
}

const defaultForm: PoolForm = {
  name: '',
  ipAddress: '',
  poolType: 'SHARED',
  reputationScore: 100,
  isActive: true,
};

export function EmailPoolsPage() {
  const { t } = useTranslation();
  const [pools, setPools] = useState<IpPool[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPool, setEditPool] = useState<IpPool | null>(null);
  const [form, setForm] = useState<PoolForm>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await apiClient.get<IpPool[]>('/api/v1/admin/email-pools');
      setPools(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const openAdd = () => {
    setEditPool(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const openEdit = (pool: IpPool) => {
    setEditPool(pool);
    setForm({
      name: pool.name,
      ipAddress: pool.ipAddress,
      poolType: pool.poolType,
      reputationScore: pool.reputationScore,
      isActive: pool.isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editPool) {
        await apiClient.patch(`/api/v1/admin/email-pools/${editPool.id}`, {
          name: form.name,
          reputationScore: form.reputationScore,
          isActive: form.isActive,
        });
      } else {
        await apiClient.post('/api/v1/admin/email-pools', form);
      }
      setShowModal(false);
      await load();
      showToast(t('emailPools.saved'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('emailPools.delete_confirm'))) return;
    await apiClient.delete(`/api/v1/admin/email-pools/${id}`);
    await load();
    showToast(t('emailPools.deleted'));
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <Breadcrumbs crumbs={[{ label: t('emailPools.title') }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('emailPools.title')}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t('emailPools.subtitle')}</p>
        </div>
        <Button onClick={openAdd}>
          <Plus className="w-4 h-4 mr-2" />
          {t('emailPools.add')}
        </Button>
      </div>

      {toast && (
        <div className="bg-green-900/40 text-green-200 px-4 py-2 rounded text-sm">{toast}</div>
      )}

      {loading ? (
        <p className="text-muted-foreground">{t('common.loading')}</p>
      ) : pools.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('emailPools.empty')}</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-3 font-medium">{t('emailPools.table.name')}</th>
                <th className="text-left p-3 font-medium">{t('emailPools.table.ip')}</th>
                <th className="text-left p-3 font-medium">{t('emailPools.table.type')}</th>
                <th className="text-left p-3 font-medium">{t('emailPools.table.reputation')}</th>
                <th className="text-left p-3 font-medium">{t('emailPools.table.active')}</th>
                <th className="text-left p-3 font-medium">{t('emailPools.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {pools.map((pool) => (
                <tr key={pool.id} className="border-t hover:bg-muted/20">
                  <td className="p-3 font-medium">{pool.name}</td>
                  <td className="p-3 font-mono text-xs">{pool.ipAddress}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      pool.poolType === 'DEDICATED'
                        ? 'bg-purple-900/40 text-purple-200'
                        : 'bg-blue-900/40 text-blue-200'
                    }`}>
                      {t(`emailPools.type.${pool.poolType}`)}
                    </span>
                  </td>
                  <td className="p-3">{pool.reputationScore}/100</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      pool.isActive ? 'bg-green-900/40 text-green-200' : 'bg-muted text-muted-foreground'
                    }`}>
                      {pool.isActive ? t('common.yes') : t('common.no')}
                    </span>
                  </td>
                  <td className="p-3 flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(pool)}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(pool.id)}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background rounded-xl shadow-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold">
              {editPool ? t('emailPools.modal.edit_title') : t('emailPools.modal.add_title')}
            </h2>
            <div className="space-y-3">
              <label className="block text-sm font-medium">{t('emailPools.modal.name')}</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              {!editPool && (
                <>
                  <label className="block text-sm font-medium">{t('emailPools.modal.ip')}</label>
                  <Input
                    value={form.ipAddress}
                    onChange={(e) => setForm({ ...form, ipAddress: e.target.value })}
                    placeholder="192.0.2.1"
                  />
                  <label className="block text-sm font-medium">{t('emailPools.modal.type')}</label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={form.poolType}
                    onChange={(e) => setForm({ ...form, poolType: e.target.value })}
                  >
                    <option value="SHARED">{t('emailPools.type.SHARED')}</option>
                    <option value="DEDICATED">{t('emailPools.type.DEDICATED')}</option>
                  </select>
                </>
              )}
              <label className="block text-sm font-medium">{t('emailPools.modal.reputation')}</label>
              <Input
                type="number"
                min={0}
                max={100}
                value={form.reputationScore}
                onChange={(e) => setForm({ ...form, reputationScore: Number(e.target.value) })}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                {t('emailPools.modal.active')}
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowModal(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={saving}>{t('common.save')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
